/**
 * TanStack Query Hook for Investments Attracted All-Time KPI
 *
 * This hook provides access to the investments attracted all-time KPI data from the database.
 * It uses TanStack Query for efficient data fetching with automatic caching,
 * background refetching, and error handling.
 *
 * Database Source: web.v_kpi_investments_attracted_all_time_v1 view
 *
 * @module useKpiInvestmentsAttractedAllTimeV1
 * @category Database Hooks
 *
 * @example
 * ```typescript
 * // Basic usage in a component
 * import { useKpiInvestmentsAttractedAllTimeV1 } from '@/hooks/database/useKpiInvestmentsAttractedAllTimeV1';
 *
 * function InvestmentsAttractedDisplay() {
 *   const { data, isLoading, error } = useKpiInvestmentsAttractedAllTimeV1();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return (
 *     <div>
 *       <h2>Investments Attracted</h2>
 *       <p>Total: {data.total_investment} SEK</p>
 *       <p>Last Updated: {new Date(data.calculation_date).toLocaleDateString()}</p>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```typescript
 * // Advanced usage with refetch control
 * function KpiDashboard() {
 *   const { data, isLoading, error, refetch, isFetching } = useKpiInvestmentsAttractedAllTimeV1();
 *
 *   const handleRefresh = () => {
 *     refetch();
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleRefresh} disabled={isFetching}>
 *         {isFetching ? 'Refreshing...' : 'Refresh Data'}
 *       </button>
 *       {data && <KpiCard kpi={data} />}
 *     </div>
 *   );
 * }
 * ```
 */

import { useQuery, UseQueryResult, QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { KpiInvestmentsAttractedAllTime } from '@/types/kpi.types';

/**
 * Fetches investments attracted all-time KPI data from the database
 *
 * This function queries the web.v_kpi_investments_attracted_all_time_v1 view and returns
 * a single row with the total investments attracted amount.
 *
 * @returns {Promise<KpiInvestmentsAttractedAllTime>} KPI data object with investments attracted amount and metadata
 * @throws {Error} If the database query fails or returns invalid data
 *
 * @internal
 */
const fetchKpiInvestmentsAttractedAllTime = async (): Promise<KpiInvestmentsAttractedAllTime> => {
  const { data, error } = await supabase
    .schema('web')
    .from('v_kpi_investments_attracted_all_time_v1')
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to fetch investments attracted all-time KPI: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from investments attracted all-time KPI query');
  }

  return data;
};

/**
 * React Hook for Investments Attracted All-Time KPI Data
 *
 * Fetches and caches the investments attracted all-time KPI data using TanStack Query.
 * The data is cached for 1 hour and will not automatically refetch on
 * window focus or component mount.
 *
 * Query Configuration:
 * - Query Key: ['kpi', 'investments-attracted-all-time', 'v1']
 * - Stale Time: 1 hour (3600000ms)
 * - Refetch on Window Focus: false
 * - Refetch on Mount: false
 * - Retry Attempts: 2
 *
 * @returns {UseQueryResult<KpiInvestmentsAttractedAllTime>} TanStack Query result object
 * @property {KpiInvestmentsAttractedAllTime | undefined} data - The KPI data (undefined during loading)
 * @property {boolean} isLoading - True if query is in initial loading state
 * @property {boolean} isFetching - True if query is fetching (including background fetches)
 * @property {boolean} isError - True if query encountered an error
 * @property {Error | null} error - The error object if query failed
 * @property {Function} refetch - Function to manually refetch the data
 *
 * @example
 * ```typescript
 * // Simple usage
 * const { data, isLoading } = useKpiInvestmentsAttractedAllTimeV1();
 * console.log(data?.total_investment); // e.g., 50000000000
 * ```
 *
 * @example
 * ```typescript
 * // Error handling
 * const { data, isLoading, isError, error } = useKpiInvestmentsAttractedAllTimeV1();
 *
 * if (isLoading) {
 *   return <Spinner />;
 * }
 *
 * if (isError) {
 *   return <ErrorAlert message={error?.message} />;
 * }
 *
 * return <KpiDisplay value={data.total_investment} />;
 * ```
 *
 * @example
 * ```typescript
 * // Manual refetch
 * const { data, refetch } = useKpiInvestmentsAttractedAllTimeV1();
 *
 * const handleUpdate = async () => {
 *   await refetch();
 *   toast.success('KPI data refreshed');
 * };
 * ```
 */
export const useKpiInvestmentsAttractedAllTimeV1 = (): UseQueryResult<KpiInvestmentsAttractedAllTime> => {
  return useQuery<KpiInvestmentsAttractedAllTime>({
    queryKey: ['kpi', 'investments-attracted-all-time', 'v1'],
    queryFn: fetchKpiInvestmentsAttractedAllTime,
    staleTime: 3600000, // 1 hour in milliseconds
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });
};

/**
 * Query Key Factory for Investments Attracted All-Time KPI
 *
 * Provides consistent query key generation for TanStack Query.
 * Useful for manual cache manipulation or query invalidation.
 *
 * @returns {string[]} The query key array
 *
 * @example
 * ```typescript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { getKpiInvestmentsAttractedAllTimeQueryKey } from '@/hooks/database/useKpiInvestmentsAttractedAllTimeV1';
 *
 * function RefreshButton() {
 *   const queryClient = useQueryClient();
 *
 *   const invalidateCache = () => {
 *     queryClient.invalidateQueries({
 *       queryKey: getKpiInvestmentsAttractedAllTimeQueryKey()
 *     });
 *   };
 *
 *   return <button onClick={invalidateCache}>Clear Cache</button>;
 * }
 * ```
 */
export const getKpiInvestmentsAttractedAllTimeQueryKey = (): string[] => {
  return ['kpi', 'investments-attracted-all-time', 'v1'];
};

/**
 * Prefetch Function for Investments Attracted All-Time KPI
 *
 * Allows prefetching the KPI data before component mount.
 * Useful for optimistic data loading and improving perceived performance.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient - TanStack Query client instance
 * @returns {Promise<void>}
 *
 * @example
 * ```typescript
 * import { useQueryClient } from '@tanstack/react-query';
 * import { prefetchKpiInvestmentsAttractedAllTime } from '@/hooks/database/useKpiInvestmentsAttractedAllTimeV1';
 *
 * function App() {
 *   const queryClient = useQueryClient();
 *
 *   useEffect(() => {
 *     // Prefetch KPI data on app load
 *     prefetchKpiInvestmentsAttractedAllTime(queryClient);
 *   }, [queryClient]);
 *
 *   return <Dashboard />;
 * }
 * ```
 */
export const prefetchKpiInvestmentsAttractedAllTime = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.prefetchQuery({
    queryKey: getKpiInvestmentsAttractedAllTimeQueryKey(),
    queryFn: fetchKpiInvestmentsAttractedAllTime,
    staleTime: 3600000,
  });
};
